import React from "react";
import styled from "styled-components";
import { typography } from "../styles/designSystem";
import { SectionHeadline } from "./Typography";
import {
    ComponentHierarchy,
    VariantVsProperty,
    TokenFlow,
    AIPipeline,
} from "./SystemDiagram";

const Section = styled.section`
    padding: 6rem 1.5rem;
    max-width: 1400px;
    margin: 0 auto;
    text-align: center;
`;

const Heading = styled(SectionHeadline)`
    text-align: center;
    margin-bottom: 4rem;
`;

const BlockGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5rem;
`;

const Block = styled.div`
    display: grid;
    grid-template-columns: 1fr 1.4fr;
    gap: 3rem;
    align-items: start;

    &:nth-child(even) {
        grid-template-columns: 1.4fr 1fr;

        @media (max-width: 1024px) {
            grid-template-columns: 1fr;
        }
    }

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
        gap: 2rem;
    }
`;

const BlockText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    text-align: left;
`;

const BlockTitle = styled.h3`
    font-family: ${typography.fontFamily.display};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    font-size: ${typography.fontSize[500]};
    font-weight: ${typography.fontWeight.bold};
    color: ${({ theme }) => theme.text};
    margin: 0 0 0.5rem 0;
    line-height: ${typography.lineHeight[300]};
`;

const BlockDescription = styled.p`
    font-size: ${typography.fontSize[300]};
    line-height: 1.8;
    color: ${({ theme }) => theme.subtleText};
    margin: 0;
`;

const BlockDiagram = styled.div`
    width: 100%;
`;

const SystemThinking = () => {
    return (
        <Section id="system-thinking">
            <Heading>Architecture Notes</Heading>
            <BlockGrid>

                {/* Block 1: Components vs Patterns vs Blocks */}
                <Block>
                    <BlockText>
                        <BlockTitle>Composition Boundaries</BlockTitle>
                        <BlockDescription>
                            Three levels, three different contracts. A component owns one
                            behaviour and exposes props. A pattern composes components and owns
                            the interaction between them — a SearchBar wires an Input, a Button,
                            and a Dropdown into one keyboard and focus model. A block composes
                            patterns and owns layout and data fetching.
                        </BlockDescription>
                        <BlockDescription>
                            Drawing those boundaries wrong is what makes a codebase expensive
                            later: state ends up in the leaf, data fetching ends up in the button,
                            and every new screen forks a component instead of composing one.
                        </BlockDescription>
                    </BlockText>
                    <BlockDiagram>
                        <ComponentHierarchy />
                    </BlockDiagram>
                </Block>

                {/* Block 2: Why Properties Beat Variants */}
                <Block>
                    <BlockDiagram>
                        <VariantVsProperty />
                    </BlockDiagram>
                    <BlockText>
                        <BlockTitle>Component API Design</BlockTitle>
                        <BlockDescription>
                            Modelling appearance as separate components multiplies the surface
                            area you have to maintain. Two variants, two sizes, two states is
                            eight components; add an icon flag and it is sixteen. Every one of
                            them is a place a bug can diverge.
                        </BlockDescription>
                        <BlockDescription>
                            Modelling it as props keeps the implementation at one and pushes the
                            permutations into the type signature, where the compiler checks them
                            and a consumer can discover them. Same reasoning as any other API:
                            narrow the implementation, widen the parameters.
                        </BlockDescription>
                    </BlockText>
                </Block>

                {/* Block 3: Designing Systems That Scale */}
                <Block>
                    <BlockText>
                        <BlockTitle>Token Pipeline</BlockTitle>
                        <BlockDescription>
                            Three layers, deliberately: raw values, the semantic decisions that
                            reference them, and the components that consume those decisions.
                            Components never reach past the middle layer, so changing a value has
                            a known blast radius instead of an unknown one.
                        </BlockDescription>
                        <BlockDescription>
                            This site runs on it. Retargeting the entire palette to a terminal
                            phosphor was a change to one token file &mdash; every page, diagram,
                            and control followed, because none of them hardcode a color.
                        </BlockDescription>
                    </BlockText>
                    <BlockDiagram>
                        <TokenFlow />
                    </BlockDiagram>
                </Block>

                {/* Block 4: AI-Driven UI */}
                <Block>
                    <BlockDiagram>
                        <AIPipeline />
                    </BlockDiagram>
                    <BlockText>
                        <BlockTitle>Programmatic Composition</BlockTitle>
                        <BlockDescription>
                            A generator emitting UI can only target what it can enumerate. Props
                            with typed, closed value sets are enumerable; a folder of
                            near-duplicate components is not. So the same API design that keeps a
                            library maintainable is what makes it addressable by a model.
                        </BlockDescription>
                        <BlockDescription>
                            That is the work at AT&amp;T: reading source design through Figma MCP
                            and emitting components a generation pipeline can consume without a
                            human redrawing them &mdash; which only holds if the component
                            contract is strict enough to validate against.
                        </BlockDescription>
                    </BlockText>
                </Block>

            </BlockGrid>
        </Section>
    );
};

export default SystemThinking;
